import type { ConstantsLoginStruct } from "@gosaas/types";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useCallback, useEffect } from "react";
import { toast } from "sonner";
import { Loader } from "@/components/Loader";
import { useAuth } from "@/components/providers/auth";
import { isAuthOAuthGoogleEnabled } from "@/lib/modules";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";
import {
	setAccessToken,
	setMfaEmail,
	setMfaToken,
	setRefreshToken,
} from "@/utils/local-storage";

export function parseGoogleOAuthCallbackSearch(search: URLSearchParams): {
	code: string | null;
	state: string | null;
} {
	const code = search.get("code");
	const state = search.get("state");
	if (!code) {
		return { code: null, state: null };
	}
	return { code, state };
}

export function hasGoogleOAuthTokens(
	data?: ConstantsLoginStruct | null,
): data is ConstantsLoginStruct & {
	access_token: string;
	refresh_token: string;
} {
	return Boolean(data?.access_token && data?.refresh_token);
}

export const Route = createFileRoute("/auth/google/callback")({
	beforeLoad: () => {
		if (!isAuthOAuthGoogleEnabled()) {
			throw redirect({ to: "/auth/login" });
		}
	},
	component: RouteComponent,
	loader: async () => {
		return parseGoogleOAuthCallbackSearch(
			new URLSearchParams(window.location.search),
		);
	},
});

export function RouteComponent() {
	const router = useRouter();
	const { refresh } = useAuth();
	const { code, state } = Route.useLoaderData();

	const handleGoogleCallback = useCallback(async () => {
		if (!code || !state) {
			toast.error("Couldn't sign in with Google", {
				description: "No code found.",
			});
			router.navigate({ to: "/auth/login" });
			return;
		}
		try {
			const decodedCode = decodeURIComponent(code);
			const response = await requestWithHandling<ConstantsLoginStruct>(() =>
				apiClient.api.oauthGoogleCallbackCreate({ code: decodedCode, state }),
			);

			if (hasGoogleOAuthTokens(response.data)) {
				const loginData = response.data;
				setAccessToken(loginData.access_token);
				setRefreshToken(loginData.refresh_token);
				refresh();
				router.navigate({ to: "/dashboard" });
			}
		} catch (error) {
			if (error instanceof TypedClientApiError) {
				if (error.code === "INVALID_EXTERNAL_TOKEN") {
					toast.error("Couldn't sign in with Google", {
						description: "The code is invalid or expired.",
					});
					router.navigate({ to: "/auth/login" });
					return;
				}
				if (error.code === "EMAIL_NOT_VERIFIED") {
					toast.error("Couldn't sign in with Google", {
						description:
							"An account with this email exists but isn't verified. Sign in with your password and verify your email, or reset your password.",
					});
					router.navigate({ to: "/auth/login" });
					return;
				}
				if (error.code === "MFA_REQUIRED") {
					const { email, mfa_token } = error.data as {
						email?: string;
						mfa_token?: string;
					};
					// Store challenge in sessionStorage instead of URL params
					if (email) {
						setMfaEmail(email);
					}
					if (mfa_token) {
						setMfaToken(mfa_token);
					}
					toast.error("Couldn't sign in with Google", {
						description: "MFA is required.",
					});
					router.navigate({ to: "/auth/mfa/required" });
					return;
				}
			}
			toast.error("Couldn't sign in with Google", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
			router.navigate({ to: "/auth/login" });
		}
	}, [code, state, refresh, router]);

	useEffect(() => {
		void handleGoogleCallback();
	}, [handleGoogleCallback]);

	return <Loader />;
}
